import { useState } from 'react'
import { Modal, Upload, Button, message, Progress, Alert, Typography, Divider } from 'antd'
import { DownloadOutlined, InboxOutlined } from '@ant-design/icons'
import type { UploadProps } from 'antd'
import skuService, { SKUImportResult } from '../../services/sku'

const { Dragger } = Upload
const { Text, Paragraph } = Typography

interface SKUImportModalProps {
  visible: boolean
  onClose: () => void
  onSuccess: () => void
}

const SKUImportModal: React.FC<SKUImportModalProps> = ({
  visible,
  onClose,
  onSuccess
}) => {
  const [uploading, setUploading] = useState(false)
  const [importResult, setImportResult] = useState<SKUImportResult | null>(null)
  const [fileList, setFileList] = useState<any[]>([])

  const handleDownloadTemplate = async () => {
    try {
      await skuService.exportTemplate()
      message.success('模板1下载成功')
    } catch (error) {
      // 错误处理已在service层处理
    }
  }

  const handleDownloadTemplate2 = async () => {
    try {
      await skuService.exportTemplate2()
      message.success('模板2下载成功')
    } catch (error) {
      // 错误处理已在service层处理
    }
  }

  const uploadProps: UploadProps = {
    name: 'file',
    multiple: false,
    fileList,
    beforeUpload: (file) => {
      // 检查文件类型
      const isExcel = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                     file.type === 'application/vnd.ms-excel'
      if (!isExcel) {
        message.error('只能上传Excel文件(.xlsx, .xls)')
        return false
      }

      // 检查文件大小 (10MB)
      const isLt10M = file.size / 1024 / 1024 < 10
      if (!isLt10M) {
        message.error('文件大小不能超过10MB')
        return false
      }

      setFileList([file])
      return false // 阻止自动上传
    },
    onRemove: () => {
      setFileList([])
      setImportResult(null)
    },
  }

  const handleImport = async () => {
    if (fileList.length === 0) {
      message.error('请选择要导入的文件')
      return
    }

    setUploading(true)
    setImportResult(null)

    try {
      // 使用自动识别模板类型的导入接口
      const result = await skuService.importSKUs(fileList[0])
      setImportResult(result)
      
      if (result.success) {
        message.success('导入成功')
        setTimeout(() => {
          onSuccess()
          handleClose()
        }, 2000)
      } else {
        message.warning('导入完成，但存在部分错误，请查看详细结果')
      }
    } catch (error) {
      // 错误处理已在service层处理
    } finally {
      setUploading(false)
    }
  }

  const handleClose = () => {
    setFileList([])
    setImportResult(null)
    setUploading(false)
    onClose()
  }

  const renderImportResult = () => {
    if (!importResult) return null

    const { totalRows, successCount, skipCount, errorCount, errors } = importResult
    const successRate = totalRows > 0 ? Math.round((successCount / totalRows) * 100) : 0

    return (
      <div style={{ marginTop: 16 }}>
        <Divider>导入结果</Divider>
        
        <Progress
          percent={successRate}
          status={successRate === 100 ? 'success' : successRate > 0 ? 'active' : 'exception'}
          format={() => `${successCount}/${totalRows}`}
        />
        
        <div style={{ marginTop: 16 }}>
          <Text>总行数: <Text strong>{totalRows}</Text></Text>
          <br />
          <Text>成功: <Text strong style={{ color: '#52c41a' }}>{successCount}</Text></Text>
          <br />
          <Text>跳过: <Text strong style={{ color: '#faad14' }}>{skipCount}</Text></Text>
          <br />
          <Text>失败: <Text strong style={{ color: '#ff4d4f' }}>{errorCount}</Text></Text>
        </div>

        {errors && errors.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <Alert
              message="导入错误详情"
              description={
                <div style={{ maxHeight: 200, overflow: 'auto' }}>
                  {errors.slice(0, 10).map((error, index) => (
                    <div key={index}>
                      第{error.row}行: {error.error}
                    </div>
                  ))}
                  {errors.length > 10 && (
                    <div style={{ marginTop: 8, color: '#666' }}>
                      还有 {errors.length - 10} 个错误未显示...
                    </div>
                  )}
                </div>
              }
              type="warning"
              showIcon
            />
          </div>
        )}
      </div>
    )
  }

  return (
    <Modal
      title="导入SKU数据"
      open={visible}
      onCancel={handleClose}
      width={600}
      footer={[
        <Button key="template1" icon={<DownloadOutlined />} onClick={handleDownloadTemplate}>
          下载模板1
        </Button>,
        <Button key="template2" icon={<DownloadOutlined />} onClick={handleDownloadTemplate2}>
          下载模板2
        </Button>,
        <Button key="cancel" onClick={handleClose}>
          取消
        </Button>,
        <Button
          key="import"
          type="primary"
          loading={uploading}
          onClick={handleImport}
          disabled={fileList.length === 0}
        >
          开始导入
        </Button>,
      ]}
    >
      <Alert
        message="导入说明"
        description={
          <div>
            <Paragraph>
              1. 请下载导入模板（支持模板1和模板2），按照模板格式填写数据
            </Paragraph>
            <Paragraph>
              2. 系统会自动识别模板类型，无需手动选择
            </Paragraph>
            <Paragraph>
              3. 条形码必须唯一，重复的条形码将被跳过
            </Paragraph>
            <Paragraph>
              4. 支持.xlsx和.xls格式的Excel文件，文件大小不能超过10MB
            </Paragraph>
          </div>
        }
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      <Dragger {...uploadProps}>
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">点击或拖拽文件到此区域上传</p>
        <p className="ant-upload-hint">
          支持单个Excel文件上传，文件大小不超过10MB
        </p>
      </Dragger>

      {renderImportResult()}
    </Modal>
  )
}

export default SKUImportModal